/**
 * AccountStatusBadge Component
 *
 * Displays the customer's account status (ACTIVE, SUSPENDED, PENDING)
 * as a styled badge with appropriate color and icon.
 *
 * Requirements: 7.1, 7.2 - Account status indicator on dashboard
 */

import { Badge } from '@bloomcode/ui';
import { CheckCircle, AlertCircle, Clock } from '@bloomcode/icons';

type AccountStatus = 'ACTIVE' | 'SUSPENDED' | 'PENDING' | 'INACTIVE';

interface AccountStatusBadgeProps {
  status: AccountStatus | string;
  className?: string;
}

const statusConfig: Record<
  AccountStatus,
  {
    label: string;
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
    icon: React.ComponentType<{ className?: string }>;
    className: string;
  }
> = {
  ACTIVE: {
    label: 'Active',
    variant: 'default',
    icon: CheckCircle,
    className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
  },
  SUSPENDED: {
    label: 'Suspended',
    variant: 'destructive',
    icon: AlertCircle,
    className: 'bg-destructive/15 text-destructive border-destructive/20',
  },
  PENDING: {
    label: 'Pending',
    variant: 'secondary',
    icon: Clock,
    className: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20',
  },
  INACTIVE: {
    label: 'Inactive',
    variant: 'outline',
    icon: AlertCircle,
    className: 'bg-muted text-muted-foreground border-muted',
  },
};

export function AccountStatusBadge({ status, className = '' }: AccountStatusBadgeProps) {
  const normalizedStatus = (status?.toUpperCase() ?? 'PENDING') as AccountStatus;
  const config = statusConfig[normalizedStatus] ?? statusConfig.PENDING;
  const Icon = config.icon;

  return (
    <Badge
      variant={config.variant}
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium ${config.className} ${className}`}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

export default AccountStatusBadge;
